import { AutomatedAssignment, QuestionCircleFilled, Schedule } from '../../icons/ContinuIcons';
import { Box, Circle, HStack, Stack, Text, Tooltip } from '@chakra-ui/react';
import { Workflow, WorkflowStats, WorkflowType } from '@/client/services/api/graphql/gql/graphql';

import { useGenerateAutomationInformation } from '@/client/components/admin/assignments/hooks/useGenerateAutomationinformation';
import { useTranslation } from 'react-i18next';

interface AssignmentDetailsStatGroupProps {
  workflowData: Workflow;
  workflowStatsData: WorkflowStats | undefined;
}

export default function AssignmentDetailsStatGroup({
  workflowData,
  workflowStatsData,
}: AssignmentDetailsStatGroupProps) {
  const { t } = useTranslation();
  const { generateAutomationInformation } = useGenerateAutomationInformation();

  const { enabled } = workflowData;

  const { type, triggerObject } = workflowData.settings;

  const isPointInTime = type === WorkflowType.PointInTime;

  return (
    <Stack spacing={4}>
      <HStack spacing={4}>
        <Box
          padding={6}
          paddingRight={24}
          backgroundColor="neutral.0"
          border="1px solid"
          borderColor="neutral.100"
          borderRadius="8px"
        >
          <Stack>
            <Text fontSize="16px" fontWeight={400}>
              <Text as="span" fontWeight={600}>
                {isPointInTime
                  ? t('admin.assignments.stats.assignmentsInProgress')
                  : t('admin.assignments.stats.assignmentStatus')}
              </Text>
            </Text>

            <HStack>
              <Circle
                size={4}
                backgroundColor={isPointInTime || enabled ? 'rgba(0, 175, 79, .2)' : 'neutral.200'}
              >
                <Circle
                  size={2}
                  backgroundColor={isPointInTime || enabled ? 'rgba(0, 175, 79, 1)' : 'neutral.400'}
                />
              </Circle>

              {isPointInTime && workflowStatsData && (
                <Text fontSize="14px" fontWeight={600} color="#00AF4F">
                  {`${workflowStatsData.inProgress} ${t('admin.assignments.stats.inProgress')}`}
                </Text>
              )}

              {!isPointInTime && (
                <Text fontSize="14px" fontWeight={600} color={enabled ? '#00AF4F' : 'neutral.400'}>
                  {enabled
                    ? t('admin.assignments.stats.active')
                    : t('admin.assignments.stats.inactive')}
                </Text>
              )}
            </HStack>
          </Stack>
        </Box>

        {workflowStatsData && (
          <HStack>
            <Box border="1px solid" borderColor="neutral.200" borderRadius="8px" padding={6}>
              <HStack>
                <Stack paddingRight={6} borderRight="1px solid" borderColor="neutral.100">
                  <Text fontSize="16px" fontWeight={600}>
                    {workflowStatsData.totalAssignments}
                  </Text>

                  <Text fontSize="16px" fontWeight={600} color="neutral.700">
                    {t('admin.assignments.stats.assigned')}
                  </Text>
                </Stack>

                <Stack paddingX={6} borderRight="1px solid" borderColor="neutral.100">
                  <Text fontSize="16px" fontWeight={600}>
                    {workflowStatsData.completedPercentage}%
                  </Text>

                  <Text fontSize="16px" fontWeight={600} color="neutral.700">
                    {t('admin.assignments.stats.completionRate')}
                  </Text>
                </Stack>

                <Stack paddingLeft={6} borderColor="neutral.200">
                  <Text fontSize="16px" fontWeight={600}>
                    {workflowStatsData.overdue}
                  </Text>

                  <Text fontSize="16px" fontWeight={600} color="neutral.700">
                    {t('admin.assignments.stats.overdue')}
                  </Text>
                </Stack>
              </HStack>
            </Box>

            {!isPointInTime && workflowStatsData.totalAssignments > 0 && (
              <HStack border="1px solid" borderColor="neutral.200" borderRadius="8px" padding={6}>
                <Schedule boxSize={14} />

                <Stack spacing={0}>
                  <Text fontSize="16px" fontWeight={600} color="baseBlack">
                    {Math.max(
                      0.5,
                      Math.round(((workflowStatsData.totalAssignments * 2.5) / 60) * 2) / 2,
                    )}
                  </Text>

                  <HStack>
                    <Text fontSize="16px" fontWeight={600} color="neutral.700">
                      {t('admin.assignments.stats.hoursSaved')}
                    </Text>

                    <Tooltip label={t('admin.assignments.stats.hoursSaved.helpText')}>
                      <QuestionCircleFilled color="warmNeutral.500" />
                    </Tooltip>
                  </HStack>
                </Stack>
              </HStack>
            )}
          </HStack>
        )}
      </HStack>

      {!isPointInTime && (
        <Box
          width="fit-content"
          border="1px solid"
          borderColor="neutral.200"
          borderRadius="8px"
          padding={6}
        >
          <HStack>
            <AutomatedAssignment boxSize={14} />

            <Stack>
              <Text fontSize="16px" fontWeight={400}>
                {generateAutomationInformation(type, triggerObject)}
              </Text>
            </Stack>
          </HStack>
        </Box>
      )}
    </Stack>
  );
}
